import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Star, Zap, Award, Clock, Share2, ShoppingCart, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ProductDetailModal = ({ product, isOpen, onClose, onShare }) => {
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState(Object.keys(product?.price || {})[0] || "");
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    if (product) {
      setSelectedSize(Object.keys(product.price || {})[0] || "");
      setQuantity(1);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, product]);

  const handleBuyNow = () => {
    if (!selectedSize) return;
    navigate("/checkout", {
      state: {
        product: {
          ...product,
          size: selectedSize,
          quantity: quantity,
        },
      },
    });
    onClose();
  };

  const handleQuantityChange = (change) => {
    setQuantity((prev) => Math.max(1, prev + change));
  };

  if (!product || !isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 bg-black/60 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Modal Content */}
      <motion.div
        className="fixed inset-4 md:inset-8 lg:inset-16 z-50 bg-white rounded-2xl overflow-y-auto font-Inter18Light"
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 50 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* Fixed Close Button */}
        <button
          onClick={onClose}
          className="fixed top-4 right-4 w-10 h-10 bg-white flex items-center justify-center rounded-full z-[100]"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        <div className="flex flex-col lg:flex-row h-full">
          {/* Image */}
          <div className="lg:w-1/2 relative bg-gradient-to-br from-[#2c3e50] to-[#34495e]">
            <motion.img
              src={product.image}
              alt={product.title}
              className="w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            <div className="absolute top-4 left-4 bg-white/90 rounded-full px-3 py-1 flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-semibold">{product.rating}</span>
              <span className="text-xs text-gray-600">({product.reviews})</span>
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-1/2 p-6 md:p-8 overflow-y-auto max-h-[90vh]">
            {/* Title in Nephilm */}
            <h2 className="text-2xl md:text-3xl font-Nephilm text-gray-800 mb-4">
              {product.title}
            </h2>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-1">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="font-medium">{product.rating}</span>
                <span className="text-gray-600">
                  ({product.reviews} reviews)
                </span>
              </div>
              <button
                onClick={onShare}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span className="text-sm">Share</span>
              </button>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              {product.fullDescription}
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#d4a574]" />
                  Nutritional Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(product.nutritionalInfo).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-gray-600 capitalize">
                        {k.replace(/([A-Z])/g, " $1")}:
                      </span>
                      <span className="font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#d4a574]" />
                  Certifications
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.certifications.map((cert) => (
                    <span
                      key={cert}
                      className="bg-[#d4a574]/10 text-[#d4a574] px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#d4a574]" />
                  Storage Instructions
                </h3>
                <p className="text-gray-600 text-sm">{product.storage}</p>
              </div>
            </div>

            {/* Size and Quantity Selection */}
            {product.price && Object.keys(product.price).length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      Select Size & Quantity
                    </h3>
                    <div className="flex flex-wrap gap-3 mb-4">
                      {Object.keys(product.price).map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                            selectedSize === size
                              ? "border-green-500 bg-green-50 text-green-700"
                              : "border-gray-300 bg-white text-gray-700 hover:border-green-300"
                          }`}
                        >
                          {size} - ${product.price[size]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="text-sm font-semibold text-gray-700">
                      Quantity:
                    </label>
                    <div className="flex items-center gap-3 bg-gray-100 rounded-lg">
                      <button
                        onClick={() => handleQuantityChange(-1)}
                        className="p-2 hover:bg-gray-200 rounded-l-lg transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="px-4 py-2 font-semibold text-gray-800 min-w-[60px] text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(1)}
                        className="p-2 hover:bg-gray-200 rounded-r-lg transition-colors"
                        aria-label="Increase quantity"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                    {selectedSize && (
                      <div className="ml-auto text-lg font-bold text-gray-800">
                        Total: ${((product.price[selectedSize] || 0) * quantity).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Buy Now Button */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <motion.button
                onClick={handleBuyNow}
                disabled={!selectedSize}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl ${
                  !selectedSize ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                Proceed to Checkout
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default ProductDetailModal;
